// Library Methods
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

// Hooks
import useTokenAccess from "src/hooks/auth/useTokenAccess";
import { useSnackbarContext } from "src/contexts/alerts/SnackbarContext";

// Types and API
import { addProgressionAnalysisComment } from "../../api";

export const useAddProgressionAnalysisComment = (
  clinicId: string | undefined
) => {
  const queryClient = useQueryClient();
  const { getToken } = useTokenAccess();
  const { t } = useTranslation();
  const { setSnackbarInfo } = useSnackbarContext();

  return useMutation({
    mutationFn: async (params: {
      patientId: string;
      progressionAnalysisId: string;
      commentData: { content: string; conditions?: string | undefined };
    }) => {
      if (!clinicId) throw new Error();

      try {
        const token = await getToken();
        return addProgressionAnalysisComment(
          token,
          clinicId,
          params.patientId,
          params.progressionAnalysisId,
          params.commentData
        );
      } catch (error) {
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["progression-report-info"] });
      queryClient.invalidateQueries({ queryKey: ["progression-access-info"] });
      queryClient.invalidateQueries({ queryKey: ["progression-report-file"] });
      setSnackbarInfo({
        variant: "success",
        message: t("Comment added successfully!"),
      });
    },
    onError: () => {
      setSnackbarInfo({
        variant: "error",
        message: t("Comment could not be added!"),
      });
    },
  });
};
